import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MDBContainer } from "mdb-react-ui-kit";
import { LoadingSpinner } from "Projects/CC_cubi_clan_project/GlobalComponents/LoadingSpinner/LoadingSpinner";
import { NavMenu } from "Projects/CC_cubi_clan_project/GlobalComponents/NavMenu/NavMenu";
import { findActiveCubeAction } from "Projects/CC_cubi_clan_project/GlobalComponents/CubeWorkspaceMenu/CubeWorkspaceMenu";
import { ListaAplicaciones } from "../../Components/ListaAplicaciones/ListaAplicaciones";
import { GestionUsuarios } from "../../Components/GestionUsuarios/GestionUsuarios";
import { AuditoriaAccesos } from "../../Components/AuditoriaAccesos/AuditoriaAccesos";
import { GestionAplicaciones } from "../../Components/GestionAplicaciones/GestionAplicaciones";
import { GestionGrupos } from "../../Components/GestionGrupos/GestionGrupos";
import { UserEdit } from "../../Components/UserEdit/UserEdit";
import IncidentWorkspace from "Projects/CC_cubi_clan_project/GlobalComponents/IncidentWorkspace/IncidentWorkspace";
import IncidentExceptionsPanel from "../../Components/IncidentExceptionsPanel/IncidentExceptionsPanel";
import IncidentLogsPanel from "../../Components/IncidentLogsPanel/IncidentLogsPanel";
import { BatchManagerPanel } from "../../Components/BatchManagerPanel/BatchManagerPanel";
import { PortalCatalogView, PortalDocumentView } from "Projects/CC_cubi_clan_project/GlobalComponents/PortalWorkspace/PortalWorkspace";
import { getCubeCapabilities, PORTAL_ITEM_TYPES } from "Projects/CC_cubi_clan_project/Utils/PortalService";
import { cubeAreaMenu, EMPTY_CUBE_CAPABILITIES } from "Projects/CC_cubi_clan_project/Utils/CubeNavigation";

function decodeDocumentId(pathname) {
  const prefix = "/portal/documentos/";
  if (!pathname.startsWith(prefix)) return null;
  try { return decodeURIComponent(pathname.slice(prefix.length).split("/")[0]); }
  catch (_) { return null; }
}

export function isCubeWorkspaceLocation(pathname) {
  return pathname === "/home" || pathname.startsWith("/home/") || pathname === "/portal" || pathname.startsWith("/portal/");
}

export default function HomeView() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setLoading] = useState(false);
  const [capabilities, setCapabilities] = useState(EMPTY_CUBE_CAPABILITIES);
  const [capabilitiesLoaded, setCapabilitiesLoaded] = useState(false);
  const areas = useMemo(() => cubeAreaMenu(capabilities), [capabilities]);
  const active = useMemo(() => findActiveCubeAction(areas, location.pathname, location.search), [areas, location.pathname, location.search]);

  useEffect(() => {
    let mounted = true;
    getCubeCapabilities()
      .then((value) => { if (mounted) setCapabilities(value); })
      .catch(() => { if (mounted) setCapabilities(EMPTY_CUBE_CAPABILITIES); })
      .finally(() => { if (mounted) setCapabilitiesLoaded(true); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!capabilitiesLoaded) return;
    // Application roots such as /mfm belong to App's sibling routes. Never
    // let this shell's fallback turn an application transition into a redirect.
    if (!isCubeWorkspaceLocation(location.pathname)) return;
    if (location.pathname === "/home" || location.pathname === "/home/") {
      navigate("/home/apps", { replace: true });
      return;
    }
    if (location.pathname === "/home/profile") return;
    if (!active) navigate("/home/apps", { replace: true });
  }, [active, capabilitiesLoaded, location.pathname, navigate]);

  const path = location.pathname;
  const documentId = decodeDocumentId(path);
  const classification = new URLSearchParams(location.search).get("class") || "";
  const go = (url) => navigate(url);

  const content = () => {
    if (path === "/home/profile") return <UserEdit onNavigate={go} onLoading={setLoading} />;
    switch (active?.action.key) {
      case "apps": return <ListaAplicaciones onNavigate={go} onLoading={setLoading} />;
      case "users-list": return <GestionUsuarios onLoading={setLoading} hideSectionNavigation />;
      case "users-audit": return <AuditoriaAccesos onLoading={setLoading} />;
      case "users-groups": return <GestionGrupos onLoading={setLoading} />;
      case "users-apps": return <GestionAplicaciones onLoading={setLoading} />;
      case "incidents-manage": return <IncidentWorkspace title="Incidencias" forceManagerView initialTab="manage" lockTab />;
      case "incidents-mine": return <IncidentWorkspace title="Mis incidencias" initialTab="mine" lockTab enableManage={false} />;
      case "incidents-exceptions": return <IncidentExceptionsPanel />;
      case "incidents-logs": return <IncidentLogsPanel />;
      case "roadmap": return <PortalCatalogView type={PORTAL_ITEM_TYPES.ROADMAP} />;
      case "decisions": return <PortalCatalogView type={PORTAL_ITEM_TYPES.DECISION} />;
      case "docs":
      case "operations-guides": return <PortalDocumentView initialDocumentId={documentId} initialClassification={classification} onSelectDocument={(id) => navigate("/portal/documentos/" + encodeURIComponent(id))} />;
      case "maintenance-processes": return <BatchManagerPanel onLoading={setLoading} />;
      default: return null;
    }
  };

  return (
    <div className="cube-home-shell">
      <LoadingSpinner isLoading={isLoading || !capabilitiesLoaded} />
      <NavMenu title="Cuberite" navMenu={[]} sessionManaged navigationVariant="workspace" navigationGroups={areas} navigationPathname={path} navigationSearch={location.search} onWorkspaceNavigate={go} onHandleNavLinks={(event, url) => {
        if (event?.force === "UserDetail" || event?.target?.name === "UserDetail") navigate("/home/profile");
        else if (url) navigate(url);
      }} onLoading={setLoading} onLoggedIn={(loggedIn) => { if (!loggedIn) navigate("/"); }} />
      <MDBContainer fluid className="px-3 px-lg-4">
        {capabilitiesLoaded ? <main id="cube-workspace-content" tabIndex="-1">{content()}</main> : null}
      </MDBContainer>
    </div>
  );
}
